import { useLocale as __useLocale } from "@/context/LocaleContext";
import { Link } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { formatPrice } from "@/lib/api";
import { FALLBACK_IMG } from "@/lib/images";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Gift, Minus, Plus, Trash2 } from "lucide-react";

const FREE_SHIPPING = 750;

export function CartDrawer() {
  const { t: __t } = __useLocale();
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, subtotal } = useStore();
  const remaining = Math.max(0, FREE_SHIPPING - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING) * 100);
  const hostedCheckout = cart.some(item => item.checkout_provider === "shopify");

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent data-testid="cart-drawer" side="right" className="w-[92vw] max-w-md bg-shule-paper border-shule-line p-0 flex flex-col">
        <SheetTitle className="px-6 h-16 flex items-center border-b border-shule-line font-serif text-2xl font-normal">{__t("Sepetin")}</SheetTitle>
        {cart.length === 0 ? (
          <div data-testid="cart-empty-state" className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center">
            <p className="font-serif text-2xl text-shule-mocha">{__t("Sepetin henüz boş")}</p>
            <p className="text-sm text-shule-soft">{__t("Anına eşlik edecek parçalar seni bekliyor.")}</p>
            <Link to="/kategori/tumu" data-testid="cart-empty-browse-button" onClick={() => setCartOpen(false)} className="btn-primary-shule">{__t("Keşfetmeye Başla")}</Link>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-shule-line bg-shule-cream/60">
              <p className="text-xs text-shule-mocha mb-2" data-testid="shipping-progress-text">
                {__t(hostedCheckout ? "Ödeme adımında hesaplanır" : remaining > 0
                  ? `Ücretsiz kargoya ${formatPrice(remaining)} kaldı`
                  : "Ücretsiz kargo kazandın")}
              </p>
              {!hostedCheckout && <div className="h-1.5 rounded-full bg-shule-line overflow-hidden">
                <div className="h-full bg-shule-gold rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
              </div>}
            </div>
            <ul className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {cart.map((item) => (
                <li key={item.key} data-testid={`cart-item-${item.slug}`} className="flex gap-3">
                  <img src={item.image || FALLBACK_IMG} alt={__t("")} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                    className="w-20 h-24 object-cover rounded-2xl border border-shule-line" />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg leading-tight truncate">{__t(item.name)}</p>
                    <p className="text-xs text-shule-soft mt-0.5">{__t(item.variant)}</p>
                    {item.giftWrap && (
                      <p className="text-xs text-shule-goldhover mt-0.5 inline-flex items-center gap-1">
                        <Gift className="w-3 h-3" />{__t(" Hediye paketli")}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-shule-line rounded-full">
                        <button data-testid={`qty-minus-${item.slug}`} onClick={() => updateQty(item.key, -1)} className="w-9 h-9 flex items-center justify-center" aria-label={__t("Azalt")}>
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span data-testid={`qty-${item.slug}`} className="w-6 text-center text-sm font-semibold">{__t(item.qty)}</span>
                        <button data-testid={`qty-plus-${item.slug}`} onClick={() => updateQty(item.key, 1)} className="w-9 h-9 flex items-center justify-center" aria-label={__t("Artır")}>
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="text-sm font-bold">{formatPrice(item.price * item.qty)}</span>
                    </div>
                  </div>
                  <button data-testid={`remove-item-${item.slug}`} onClick={() => removeFromCart(item.key)} className="self-start w-9 h-9 flex items-center justify-center text-shule-soft hover:text-shule-coffee" aria-label={__t("Kaldır")}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="px-6 py-5 border-t border-shule-line bg-shule-cream/60">
              <div className="flex justify-between items-baseline mb-4">
                <span className="text-sm text-shule-mocha">{__t("Ara Toplam")}</span>
                <span data-testid="cart-subtotal" className="font-serif text-2xl">{formatPrice(subtotal)}</span>
              </div>
              <Link to="/sepet" data-testid="go-to-cart-button" onClick={() => setCartOpen(false)} className="btn-primary-shule w-full">{__t("Sepete Git")}</Link>
              <p className="text-[11px] text-shule-soft text-center mt-3">{__t("Teslimat ve ödeme bilgilerini güvenli ödeme ekranında tamamlayabilirsin.")}</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
